/**
 * Build the campaign font CSS from the campaign's stored googleFont + fontClass.
 *
 * IRL2-2864: the canonical font system in CC stores fonts as bare Google Fonts
 * family names (e.g. "Montserrat"). Legacy campaigns (notably prodc
 * ask-africa-survey) store googleFont as a full `@import url(...)` CSS rule
 * and fontClass as a full `font-family: ...` rule. This helper accepts both
 * shapes so old and new campaigns render identically.
 *
 * IRL2-2988 (prodc backport): campaigns may store a raw `@font-face { ... }`
 * block directly in googleFont (e.g. Felix / Benguiat). Previously this fell
 * through to the bare-name branch and produced a junk googleapis URL, so the
 * font never registered. Now detected and passed through byte-for-byte as
 * fontFaceRule, injected before the html/input/button family rules.
 *
 * **ASK-AFRICA SAFETY:** when googleFont starts with `@import` and fontClass
 * starts with `font-family`, both values pass through byte-for-byte, so
 * ask-africa-survey on prodc renders exactly as it does today.
 */

export interface CampaignFontCss {
  /** The `@import url(...)` rule to inject at the top of the stylesheet, or '' when no font is configured. */
  importRule: string
  /** A complete `@font-face { ... }` rule for a raw custom font block, or '' when none. Inject before the family rules. */
  fontFaceRule: string
  /** A complete `font-family: ...;` CSS declaration suitable for inserting inside `html { }`, `input { }`, `button { }`. */
  fontFamilyRule: string
}

const FALLBACK_FONT_FAMILY_RULE = 'font-family: Arial, Helvetica, sans-serif;'

function startsWithCi(value: string, prefix: string): boolean {
  return value.toLowerCase().startsWith(prefix.toLowerCase())
}

function extractFamilyFromImport(importRule: string): string {
  const match = importRule.match(/family=([^:&)]+)/i)
  if (!match) return ''
  try {
    return decodeURIComponent(match[1]).replace(/\+/g, ' ').trim()
  } catch {
    return match[1].replace(/\+/g, ' ').trim()
  }
}

function buildImportRuleFromBareName(fontName: string): string {
  // Use specific weight tuples (400 regular, 700 bold) rather than a variable
  // axis range. Google Fonts API returns 503 for variable-range requests
  // (`wght@0,100..900;1,100..900`) when the requested font is NOT a variable
  // font — e.g. Poppins, Bebas Neue, Anton. The `wght@400;700` form works
  // for both variable and static fonts: variable fonts honour the
  // requested points within their axis; static fonts return those weights
  // (or the closest available). Italic is browser-synthesised when missing,
  // which is acceptable for campaign body copy.
  return `@import url('https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;700&display=swap');`
}

function buildFontFamilyRuleFromName(fontName: string): string {
  return `font-family: "${fontName}", sans-serif;`
}

export function buildCampaignFontCss(
  rawGoogleFont: string | null | undefined,
  rawFontClass: string | null | undefined,
): CampaignFontCss {
  const googleFont = (rawGoogleFont ?? '').trim()
  const fontClass = (rawFontClass ?? '').trim()

  let importRule = ''
  let fontFaceRule = ''
  let derivedFamilyName = ''

  if (googleFont) {
    if (startsWithCi(googleFont, '@font-face')) {
      // IRL2-2988: raw @font-face block stored in googleFont (e.g. Felix /
      // Benguiat). Pass through byte-for-byte; family comes from fontClass.
      fontFaceRule = googleFont
    } else if (startsWithCi(googleFont, '@import')) {
      importRule = googleFont
      derivedFamilyName = extractFamilyFromImport(googleFont)
    } else {
      importRule = buildImportRuleFromBareName(googleFont)
      derivedFamilyName = googleFont
    }
  }

  let fontFamilyRule: string
  if (startsWithCi(fontClass, 'font-family')) {
    fontFamilyRule = fontClass.endsWith(';') ? fontClass : `${fontClass};`
  } else if (derivedFamilyName) {
    fontFamilyRule = buildFontFamilyRuleFromName(derivedFamilyName)
  } else {
    fontFamilyRule = FALLBACK_FONT_FAMILY_RULE
  }

  return { importRule, fontFaceRule, fontFamilyRule }
}
